import React from 'react'
import { Component, ReactNode, ErrorInfo } from 'react'
import { ErrorFallback } from './ui/error-fallback'
import { logger } from '@/services/logger'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo?: ErrorInfo
  errorId?: string
}

export class ErrorBoundary extends React.Component<React.PropsWithChildren<Props>, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error
    logger.error('ErrorBoundary caught an error', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    })

    // Generate error ID for tracking
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    this.setState({
      errorInfo,
      errorId,
    })

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)
  }

  private handleRetry = () => {
    // Reset error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: undefined,
      errorId: undefined,
    })
  }

  private handleRefresh = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
          <div className="w-full max-w-2xl">
            <ErrorFallback
              error={this.state.error || undefined}
              title="Application Error"
              description="Something went wrong in the application. This error has been logged and our team has been notified. You can try refreshing the page or contact support if the problem persists."
              onRetry={this.handleRetry}
              showDetail={true}
              className="w-full"
            />

            {/* Additional Action Buttons */}
            <div className="mt-4 flex justify-center gap-3">
              <button
                onClick={this.handleRefresh}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Refresh Page
              </button>
              <button
                onClick={() => window.history.back()}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Go Back
              </button>
            </div>

            {/* Error ID for support */}
            {this.state.errorId && (
              <div className="mt-3 text-center">
                <span className="text-xs text-gray-500">Error ID: {this.state.errorId}</span>
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
